import React, { memo } from 'react';
import { List } from 'immutable';

import BaseInput from '../../helpers/Input';
import BaseTextarea from '../../helpers/Textarea';
import PrettyError from '../../decorators/PrettyError';
import DisplayError from '../../decorators/DisplayError';
import Popup from '../../helpers/Popup';
import Button from '../../helpers/Button';
import Checkbox from '../../helpers/Checkbox';
import FormGroup from '../../helpers/FormGroup';
import Table from '../../helpers/AgGridTable';
import config from '../../../config';
import { GridCard } from '../../helpers/GridCard';
import { GridCheckbox } from '../../helpers/GridCheckbox';

import style from './style.module.css';

const Input = PrettyError(DisplayError(BaseInput));
const Textarea = PrettyError(DisplayError(BaseTextarea));
const columnDefs = config.tables.getIn(['project', 'approvalListIds', 'columns']);

const CheckboxCellRenderer = ({ onChange }) => <GridCheckbox onChange={onChange} />;

const AddAddonProjectPopup = props => {
  const {
    approvalList,
    addOnProjectCreate,
    isValid,
    suggestUser,
    suggestClient,
    suggestExecutive,
    onChange,
    onChangeApprovalListIds,
    onSave,
    loading,
  } = props;
  const checkedApprovalListId = new Set();
  const disabledButton = !isValid || loading;

  const notEmptyApprovalList = approvalList
    .get('list')
    .filter(approval => approval.get('projectApprovalLists').size > 0)
    .map(approval => ({
      ...approval.toJS(),
      projectApprovalLists: approval
        .get('projectApprovalLists')
        .map(projectApprovalList => ({
          ...projectApprovalList.toJS(),
          projectCategory: approval.get('projectCategory'),
        }))
        .toJS(),
    }));

  const data = [];

  notEmptyApprovalList.forEach(approval => {
    data.push(...approval.projectApprovalLists);
  });

  const footer = (
    <Button className="btn-primary" disabled={disabledButton} onClick={onSave}>
      Save
    </Button>
  );

  const getColumnDefs = () => {
    const columns = columnDefs.map(columnDef => {
      const field = columnDef.get('field');

      switch (field) {
        case 'selected':
          return columnDef.set('cellRendererFramework', params => (
            <CheckboxCellRenderer onChange={() => handleChangeClick(params)} />
          ));

        default:
          return columnDef;
      }
    });

    return columns;
  };

  const handleChangeClick = params => {
    const {
      colDef: { field },
      data: { approvalListId },
    } = params;

    if (field === 'selected') {
      if (checkedApprovalListId.has(approvalListId)) {
        checkedApprovalListId.delete(approvalListId);
      } else {
        checkedApprovalListId.add(approvalListId);
      }

      onChangeApprovalListIds('approvalListIds', [...checkedApprovalListId]);
    }
  };

  const getRowNodeId = data => data.approvalListId;

  return (
    <Popup className={style.host} footer={footer} header="Add Add-on Project" id="newAddOnProject">
      <div className="row">
        <FormGroup className="col-md-6">
          <Input
            className="input-sm"
            label="Project Label"
            name="category"
            onChange={onChange}
            root={addOnProjectCreate}
            autoFocus
            canDisplayError
            deep
            required
            showLabel
          />
        </FormGroup>
        <div className="col-md-6">{suggestUser}</div>
        <div className="col-md-6">{suggestClient}</div>
        <div className="col-md-6">{suggestExecutive}</div>
        <FormGroup className="col-md-12">
          <Checkbox
            checked={addOnProjectCreate.get('active')}
            label="Active"
            name="active"
            onChange={onChange}
            onClick={event => event.stopPropagation()}
          />
        </FormGroup>
        <FormGroup className="col-md-12">
          <Textarea
            label="Project Scope"
            name="details"
            onChange={onChange}
            placeholder=""
            root={addOnProjectCreate}
            rows="4"
            deep
            showLabel
          />
        </FormGroup>
        <FormGroup className="col-md-12">
          <Textarea
            label="Status"
            name="status"
            onChange={onChange}
            placeholder=""
            root={addOnProjectCreate}
            rows="4"
            deep
            showLabel
          />
        </FormGroup>
        <FormGroup className="col-md-12">
          <label className="field-label">
            Approval Lists (Select which lists, if any, to move to the new Project tab)
          </label>
          <GridCard className={style.approvalList}>
            <Table
              columnDefs={getColumnDefs()}
              getRowNodeId={getRowNodeId}
              isFetching={approvalList.get('loading')}
              rowData={List(data)}
            />
          </GridCard>
        </FormGroup>
      </div>
    </Popup>
  );
};

export default memo(AddAddonProjectPopup);
